// Theme registry — single source of truth for the picker UI and the API zod schema.
// Adding a new theme: add a CSS block in styles/globals.css, then a row here.

export const THEME_IDS = [
  // Vibe / futuristic — surfaced first in the picker
  'neon',
  'synthwave',
  'plasma',
  'aurora',
  'glass',
  'mono',
  // Brand-aligned + curated
  'alpha',
  'setswana',
  'sunset',
  'coral',
  'forest',
  'candy',
  // Utility
  'light',
  'dark',
  'slate',
  'ocean',
  'high-contrast',
] as const;

export type ThemeId = (typeof THEME_IDS)[number];

export type ThemeMeta = {
  id: ThemeId;
  label: string;
  description: string;
  // Preview swatches (hex strings, used by ThemeSwatch). Read directly here so
  // the picker can render previews before that theme has ever been applied.
  preview: {
    bg: string;
    surface: string;
    text: string;
    accent: string;
  };
  // Optional grouping label rendered as a section header in the picker.
  group?: 'vibe' | 'brand' | 'utility';
};

export const THEMES: ThemeMeta[] = [
  // ─── Vibe / futuristic ──────────────────────────────────────────────────
  {
    id: 'neon',
    label: 'Neon',
    description: 'Cyberpunk — neon pink + cyan on deep black. Glows.',
    preview: { bg: '#0D0D17', surface: '#161626', text: '#F0F0FF', accent: '#EC4899' },
    group: 'vibe',
  },
  {
    id: 'synthwave',
    label: 'Synthwave',
    description: 'Retro-futurist sunset horizon. Pink + indigo, animated mesh.',
    preview: { bg: '#120B23', surface: '#1E1237', text: '#FFEBF5', accent: '#F472B6' },
    group: 'vibe',
  },
  {
    id: 'plasma',
    label: 'Plasma',
    description: 'Flowing violet + blue energy field. Animated.',
    preview: { bg: '#0C081E', surface: '#161032', text: '#EBE6FF', accent: '#8B5CF6' },
    group: 'vibe',
  },
  {
    id: 'aurora',
    label: 'Aurora',
    description: 'Northern lights. Deep purple with teal accent and animated mesh.',
    preview: { bg: '#140E26', surface: '#20163A', text: '#F0EBFF', accent: '#2DD4BF' },
    group: 'vibe',
  },
  {
    id: 'glass',
    label: 'Glass',
    description: 'Frosted glass over a soft pastel mesh. Light and airy.',
    preview: { bg: '#FAFAFC', surface: '#FFFFFF', text: '#1E193C', accent: '#6366F1' },
    group: 'vibe',
  },
  {
    id: 'mono',
    label: 'Mono',
    description: 'Pure black + white with gold accent. Luxury minimalism.',
    preview: { bg: '#F0F0F0', surface: '#FAFAFA', text: '#0A0A0A', accent: '#B48E3C' },
    group: 'vibe',
  },

  // ─── Brand-aligned + curated ────────────────────────────────────────────
  {
    id: 'alpha',
    label: 'Alpha Default',
    description: 'Brand navy + orange. The Alpha Direct standard.',
    preview: { bg: '#F3F4F6', surface: '#FFFFFF', text: '#111827', accent: '#0B1272' },
    group: 'brand',
  },
  {
    id: 'setswana',
    label: 'Setswana',
    description: 'Earthy ochre and acacia. Warm, rooted, brand navy as accent.',
    preview: { bg: '#F0E2C6', surface: '#FEFAF4', text: '#382010', accent: '#0B1272' },
    group: 'brand',
  },
  {
    id: 'sunset',
    label: 'Sunset',
    description: 'Warm pink and dusk gradients. Romantic, energetic.',
    preview: { bg: '#FCE4D6', surface: '#FFFBF6', text: '#43143C', accent: '#DB2777' },
    group: 'brand',
  },
  {
    id: 'coral',
    label: 'Coral',
    description: 'Friendly peach and warm orange. Bright and energetic.',
    preview: { bg: '#FED7C0', surface: '#FFFAF5', text: '#712913', accent: '#EA580C' },
    group: 'brand',
  },
  {
    id: 'forest',
    label: 'Forest',
    description: 'Calming moss greens. Easy on the eyes for long sessions.',
    preview: { bg: '#C8E6C8', surface: '#F5FAF4', text: '#143C1E', accent: '#166534' },
    group: 'brand',
  },
  {
    id: 'candy',
    label: 'Candy',
    description: 'Magenta meets fuchsia. Playful, sweet, hard to ignore.',
    preview: { bg: '#FCD2F0', surface: '#FFFAFE', text: '#4C094B', accent: '#C026D3' },
    group: 'brand',
  },

  // ─── Utility ────────────────────────────────────────────────────────────
  {
    id: 'light',
    label: 'Light',
    description: 'Crisp white surfaces, brand orange as the primary action.',
    preview: { bg: '#F7F7F7', surface: '#FFFFFF', text: '#171717', accent: '#FF6600' },
    group: 'utility',
  },
  {
    id: 'dark',
    label: 'Dark',
    description: 'True dark, OLED-friendly. Easier on the eyes after sundown.',
    preview: { bg: '#0A0A0A', surface: '#171717', text: '#FAFAFA', accent: '#FF8533' },
    group: 'utility',
  },
  {
    id: 'slate',
    label: 'Slate',
    description: 'Neutral grey, low contrast — comfortable for long sessions.',
    preview: { bg: '#E2E8F0', surface: '#F8FAFC', text: '#0F172A', accent: '#0B1272' },
    group: 'utility',
  },
  {
    id: 'ocean',
    label: 'Ocean',
    description: 'Cool blue-tinted surfaces. An alternative to brand navy.',
    preview: { bg: '#E0F2FE', surface: '#F8FAFC', text: '#082F49', accent: '#075985' },
    group: 'utility',
  },
  {
    id: 'high-contrast',
    label: 'High Contrast',
    description: 'WCAG AAA. Maximum readability for accessibility.',
    preview: { bg: '#FFFFFF', surface: '#FFFFFF', text: '#000000', accent: '#000000' },
    group: 'utility',
  },
];

export const isThemeId = (s: unknown): s is ThemeId =>
  typeof s === 'string' && (THEME_IDS as readonly string[]).includes(s);
